<template>
  <section>
    <div class="d-flex align-items-center justify-content-between mb-1">
      <div class="d-flex align-items-center">
        <h1 class="title mr-3">{{ $t('entity.users') }}</h1>
        <button class="btn btn-link btn-with-icon" @click="createUser">
          <span class="icon icon-plus"></span
          >{{ isNeuroAdminManage ? $t('button.addAdmin') : $t('button.addUser') }}
        </button>
      </div>
    </div>
    <filter-result
      v-if="filterResultData && isReady"
      :data="filterResultData"
      :headers="headers"
    ></filter-result>

    <base-table
      :no-data="!hasUsers"
      :headers="headers"
      :loading="loading"
      :sorting="sorting"
      @filter="filterData"
      @sorting="changeSorting"
      @isReady="isReadyTable"
    >
      <template #tbody>
        <user-table-row
          v-for="user in users"
          :key="user.id"
          :user="user"
          @click-reset="startResetUserPwd"
        ></user-table-row>
      </template>
    </base-table>
    <div class="mt-3">
      <paginator
        v-if="showPaginator"
        :total="totalCount"
        :per-page="perPage"
        v-model="currentPage"
        @change:count="numberOfElements"
      ></paginator>
    </div>

    <user-reset-password
      v-if="resetUser"
      :user="resetUser"
      @cancel="throwResetUser"
      @reset="saveResetUser"
    ></user-reset-password>
  </section>
</template>

<script>
import http from '@/modules/http.js'
import { stringify } from 'qs'
import { userStatus } from '@/main.config.js'
import { paginator } from '@/mixins/paginator.js'
import { getStatusListForFilter } from '@/modules/status-list.js'
import { roleFilterList } from '@/modules/role-list.js'
import { userRights } from '@/mixins/user-rights.js'
import { pageFiltersInQuery } from '@/components/Filters/mixins/page-filters-in-query'
import { systemUnavailable } from '@/mixins/system-unavailable.js'

export default {
  name: 'UsersList',
  mixins: [paginator, userRights, pageFiltersInQuery, systemUnavailable],
  components: {
    BaseTable: () => import('@/base/BaseTable.vue'),
    UserResetPassword: () => import('@/components/UserResetPassword.vue'),
    UserTableRow: () => import('@/components/table-rows/UserTableRow.vue'),
    FilterResult: () => import('@/components/Filters/FilterResult.vue')
  },
  data: () => ({
    loading: true,
    users: [],
    status: 0,
    userRole: '0',
    resetUser: null,
    sorting: {
      by: 'name',
      order: 'asc'
    },
    isReady: false
  }),
  computed: {
    statusList() {
      return getStatusListForFilter('user')
    },
    sortParams() {
      const sortOrder = this.sorting.order === 'asc' ? 1 : -1
      if (this.sorting.by === 'name') {
        return {
          surname: sortOrder,
          name: sortOrder,
          middle_name: sortOrder
        }
      } else if (this.sorting.by === 'position') {
        return {
          position: sortOrder
        }
      } else if (this.sorting.by === 'department') {
        return {
          department: sortOrder
        }
      }
      return {
        id: sortOrder
      }
    },
    params() {
      let params = Object.assign({}, this.pageParams)

      params['$sort'] = this.sortParams
      if (this.showRole) {
        params['user_role.title'] = this.showRole
      } else if (this.userRole && this.userRole !== '0') {
        params['user_role.title'] = this.userRole
      }

      if (this.status) {
        params['user.status'] = this.status
      } else if (Object.prototype.hasOwnProperty.call(params, 'user.status')) {
        delete params['user.status']
      }

      if (this.$store.getters.SELECTED_HOSPITAL) {
        params['hospital_id'] = this.$store.getters.SELECTED_HOSPITAL
      }

      return params
    },
    filterResultData() {
      let data = {}

      if (this.status) {
        data[this.$t('general.status')] = this.statusList[this.status].title
      }

      if (this.userRole && this.userRole !== '0') {
        data[this.$t('personInfo.role')] = roleFilterList[this.userRole].title
      }

      return data
    },
    showRole() {
      return this.$route.meta.showRole
    },
    hasUsers() {
      return this.users.length > 0
    },
    headers() {
      let title = this.isNeuroAdmin ? `№` : this.$t('personInfo.fullName')
      return {
        name: {
          title: title,
          class: 'th-name',
          sorting: 'asc'
        },
        role: {
          title: this.$t('personInfo.role'),
          class: 'th-mid',
          filter: this.showRole ? false : true,
          options: roleFilterList,
          selected: this.userRole
        },
        position: {
          title: this.$t('personInfo.position'),
          sorting: 'asc'
        },
        phone: {
          title: this.$t('personInfo.phone'),
          class: 'th-phone'
        },
        department: {
          title: this.$t('personInfo.department'),
          class: 'th-empty',
          sorting: 'asc'
        },
        status: {
          title: this.$t('general.status'),
          class: 'th-mid',
          filter: true,
          options: this.statusList,
          selected: this.status
        }
      }
    }
  },
  watch: {
    status() {
      this.currentPage = 1
      this.loadData()
    },
    userRole() {
      this.currentPage = 1
      this.loadData()
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      this.isReady = false

      http
        .get('/users', {
          params: this.params,
          paramsSerializer: params =>
            stringify(params, {
              arrayFormat: 'indices',
              encodeValuesOnly: true
            })
        })
        .then(response => response.data)
        .then(data => {
          this.totalCount = data.total
          this.users = data.data
        })
        .catch(error => console.log(error))
        .finally(() => (this.loading = false))
    },
    filterData(event) {
      if (event.key === 'status') {
        this.status = +event.value
      } else if (event.key === 'role') {
        this.userRole = event.value
      }
      this.updateFullQuery()
    },
    startResetUserPwd(user) {
      this.resetUser = user
    },
    throwResetUser() {
      this.resetUser = null
    },
    saveResetUser(user) {
      this.throwResetUser()
      let index = this.users.indexOf(user)
      if (index === -1) {
        return
      }
      this.users[index].status = userStatus.unregistered
    },
    changeSorting(sortingData) {
      this.sorting = sortingData
      this.loadData()
    },
    numberOfElements(number) {
      this.perPage = number
    },
    isReadyTable(value) {
      this.isReady = value
    },
    createUser() {
      if (Number(localStorage.getItem('activeHospital'))) {
        this.$router.push({ name: this.isNeuroAdminManage ? 'admin-add' : 'user-add' })
      } else {
        this.$emit('system:unavailable')
      }
    }
  }
}
</script>
